import axios, { AxiosInstance } from 'axios';
import axiosInstance from '@/api/axiosInstance';
import {
  TranscriptionJob,
  QueueStatistics,
  JobListRequest,
  JobListResponse,
  BulkActionRequest,
  BulkActionResponse,
  Alert,
  QueueMetrics,
  ChartData,
  ExportOptions,
  WorkerStatus,
} from '../types';
import { TimeRange } from '../slices/metricsSlice';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001';

class TranscriptionQueueApi {
  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: `${API_BASE_URL}/api/transcription`,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.axiosInstance.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  async getJobs(request: JobListRequest): Promise<JobListResponse> {
    const response = await this.axiosInstance.post('/jobs/list', request);

    // Transform API response to match frontend interface
    const transformedJobs = response.data.jobs.map((job: any) => this.transformJob(job));

    return {
      jobs: transformedJobs,
      pagination: response.data.pagination,
      statistics: response.data.statistics,
    };
  }

  private transformJob(apiJob: any): TranscriptionJob {
    // Pass through API response, mapping snake_case to our interface
    return {
      id: apiJob.id,
      recording_id: apiJob.recording_id || '',
      recordingId: apiJob.recording_id || '',
      title: apiJob.title || null,
      status: apiJob.status || 'pending',
      caller_number: apiJob.caller_number,
      caller_name: apiJob.caller_name || null,
      callee_number: apiJob.callee_number,
      callee_name: apiJob.callee_name || null,
      direction: apiJob.direction || 'inbound',
      call_date: apiJob.call_date || null,
      duration: apiJob.duration || 0,
      created: apiJob.created,
      updated: apiJob.updated,
      transcript_status: apiJob.transcript_status || 'pending',
      transcript_id: apiJob.transcript_id,
      transcript_text: apiJob.transcript_text,
      has_transcript: apiJob.has_transcript || false,
      processing_time: apiJob.processing_time,
      error_message: apiJob.error_message || null,
      company: apiJob.company,
      user: apiJob.user,
      download_url: apiJob.download_url,
      // Employee and owner info
      employee_name: apiJob.employee_name || null,
      employee_id: apiJob.employee_id || null,
      owner_name: apiJob.owner_name || null,
      owner_email: apiJob.owner_email || null,
      is_configured: apiJob.is_configured ?? true,
      // Legacy compatibility fields
      callId: apiJob.call_id,
      companyId: apiJob.company_id || '',
      companyName: apiJob.company || 'Unknown',
      userId: apiJob.user_id || '',
      userName: apiJob.user || 'Unknown User',
      progress: apiJob.progress || 0,
      priority: apiJob.priority || 'normal',
      metadata: {
        duration: apiJob.duration || 0,
      },
      timestamps: {
        created: apiJob.created,
        updated: apiJob.updated,
      },
      // Processing stage tracking
      processing_stages: apiJob.processing_stages || [],
      completed_stages: apiJob.completed_stages || [],
      current_stage: apiJob.current_stage || null,
      has_score: apiJob.has_score || false,
    };
  }

  async getJobDetails(jobId: string): Promise<TranscriptionJob> {
    const response = await this.axiosInstance.get(`/jobs/${jobId}`);
    return this.transformJob(response.data);
  }

  async retryJob(jobId: string): Promise<TranscriptionJob> {
    const response = await this.axiosInstance.post(`/jobs/${jobId}/retry`);
    return response.data;
  }

  async cancelJob(jobId: string): Promise<void> {
    await this.axiosInstance.delete(`/jobs/${jobId}`);
  }

  async performBulkAction(request: BulkActionRequest): Promise<BulkActionResponse> {
    const response = await this.axiosInstance.post('/jobs/bulk-action', request);
    return response.data;
  }

  async getStatistics(): Promise<QueueStatistics> {
    const response = await this.axiosInstance.get('/queue/stats');
    return response.data;
  }

  async getWorkerStatus(): Promise<WorkerStatus> {
    // Use the global axios instance with Auth0 authentication
    const response = await axiosInstance.get('/api/private/transcription-queue/stats');
    return response.data.worker_status;
  }

  async startWorker(limit: number = 10): Promise<{
    success: boolean;
    message: string;
    pending_count: number;
    process_id?: number;
  }> {
    // Use the global axios instance with Auth0 authentication
    const response = await axiosInstance.post('/api/private/transcription-queue/start', { limit });
    return response.data;
  }

  async getMetrics(timeRange: TimeRange): Promise<{
    metrics: QueueMetrics[];
    queueDepthChart: ChartData[];
    processingRateChart: ChartData[];
    successRateChart: ChartData[];
    errorDistribution: Record<string, number>;
    performanceByCompany: Array<{
      company: string;
      avgProcessingTime: number;
      successRate: number;
      totalJobs: number;
    }>;
  }> {
    const response = await this.axiosInstance.get(`/analytics/metrics`, {
      params: { timeRange },
    });
    return response.data;
  }

  async getErrorDistribution(timeRange: TimeRange): Promise<Record<string, number>> {
    const response = await this.axiosInstance.get('/analytics/errors', {
      params: { timeRange },
    });
    return response.data;
  }

  async getAlerts(): Promise<Alert[]> {
    const response = await this.axiosInstance.get('/alerts');
    return response.data;
  }

  async createAlert(alertData: Omit<Alert, 'id' | 'created' | 'triggerCount'>): Promise<Alert> {
    const response = await this.axiosInstance.post('/alerts', alertData);
    return response.data;
  }

  async updateAlert(id: string, data: Partial<Alert>): Promise<Alert> {
    const response = await this.axiosInstance.put(`/alerts/${id}`, data);
    return response.data;
  }

  async deleteAlert(id: string): Promise<void> {
    await this.axiosInstance.delete(`/alerts/${id}`);
  }

  async acknowledgeAlert(alertId: string): Promise<void> {
    await this.axiosInstance.post(`/alerts/${alertId}/acknowledge`);
  }

  async exportJobs(options: ExportOptions): Promise<Blob> {
    const response = await this.axiosInstance.post('/jobs/export', options, {
      responseType: 'blob',
    });
    return response.data;
  }

  async getJobLogs(jobId: string): Promise<string[]> {
    const response = await this.axiosInstance.get(`/jobs/${jobId}/logs`);
    return response.data;
  }

  async downloadTranscript(jobId: string): Promise<Blob> {
    const response = await this.axiosInstance.get(`/jobs/${jobId}/transcript`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async searchJobs(query: string): Promise<TranscriptionJob[]> {
    const response = await this.axiosInstance.get('/jobs/search', {
      params: { q: query },
    });
    return response.data;
  }

  async forceTranscribe(recordingId: string): Promise<{
    success: boolean;
    message: string;
    recording_id?: string;
    call_transcript_id?: number;
    error?: string;
  }> {
    // Use the global axios instance with Auth0 authentication
    const response = await axiosInstance.post(
      `/api/private/transcription-queue/${recordingId}/force-transcribe`
    );
    return response.data;
  }

  async getCompanySuggestions(query: string): Promise<Array<{ id: string; name: string }>> {
    const response = await this.axiosInstance.get('/companies/suggest', {
      params: { q: query },
    });
    return response.data;
  }

  async getUserSuggestions(query: string): Promise<Array<{ id: string; name: string }>> {
    const response = await this.axiosInstance.get('/users/suggest', {
      params: { q: query },
    });
    return response.data;
  }
}

export const transcriptionQueueApi = new TranscriptionQueueApi();